import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';

import IconButton from 'components/common/IconButton';
import Menu, { MenuItem } from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import IconBlurb from 'components/icons/IconBlurb';
import IconMoreVert from 'components/icons/IconMoreVert';
import OrderNotes from 'components/workflow/OrderNotes';

import { deleteOrder } from 'redux/modules/orders';

const styles = theme => ({
  root: {
    maxWidth: 95,
    borderRadius: 5,
    border: `1px solid ${theme.palette.primary.A100}`,
  },
  toolBar: {
    position: 'relative',
    width: '100%',
    justifyContent: 'space-around',
    '&:after': {
      content: '"."',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: '50%',
      width: 1,
      background: theme.palette.primary.A100,
    },
  },
  iconButton: {
    margin: -6.5,
    color: theme.palette.grey[600],
  },
  iconBlurb: {
    width: 20,
    height: 20,
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
});

class OrderViewBarActions extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      moreMenuOpen: false,
      deleteMenuOpen: false,
      notesMenuOpen: false,
    };

    this.onMenuClose = this.onMenuClose.bind(this);
    this.onMoreMenuButtonClick = this.onMoreMenuButtonClick.bind(this);
    this.onNotesMenuButtonClick = this.onNotesMenuButtonClick.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onMoreMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      moreMenuOpen: true,
      deleteMenuOpen: false,
      notesMenuOpen: false,
    });
  }

  onDeleteMenuItemClick() {
    this.setState({ deleteMenuOpen: true });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    const { ID, actions } = this.props;
    actions.deleteOrder({ ID, triggerLoading: true });
    this.setState({ anchorEl: null });
  }

  onNotesMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      moreMenuOpen: false,
      notesMenuOpen: true,
      deleteMenuOpen: false,
    });
  }

  render() {
    const { ID, Patient, history, account, archive, classes, business, ...otherProps } = this.props;
    const { moreMenuOpen, notesMenuOpen, anchorEl, deleteMenuOpen } = this.state;
    return (
      <Paper className={classes.root} elevation={1}>
        <Toolbar disableGutters className={classes.toolBar}>
          <IconButton
            className={classes.iconButton}
            aria-label="Notes"
            aria-owns={Boolean(anchorEl) && notesMenuOpen
              ? `${ID}-order-notes-menu` : null}
            aria-haspopup="true"
            onClick={this.onNotesMenuButtonClick}
            tooltipContent="Notes"
          >
            <IconBlurb className={classes.iconBlurb} />
          </IconButton>
          <Menu
            id={`${ID}-order-notes-menu`}
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && notesMenuOpen}
            onClose={this.onMenuClose}
            padding="none"
            maxHeight={350}
            list={false}
          >
            <OrderNotes
              {...{ 'Author ID': account.ID }}
              {...{ 'Order ID': ID }}
              {...{ 'Order Notes': otherProps.Notes }}
              disabled={archive}
              business={business}
            />
          </Menu>
          <IconButton
            className={classes.iconButton}
            aria-label="More"
            aria-owns={Boolean(anchorEl) && (moreMenuOpen || deleteMenuOpen)
              ? `${ID}-order-more-menu` : null}
            aria-haspopup="true"
            onClick={this.onMoreMenuButtonClick}
            tooltipContent="Open Menu"
          >
            <IconMoreVert className={classes.iconMoreVert} />
          </IconButton>
          <Menu
            id={`${ID}-order-more-menu`}
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && (moreMenuOpen || deleteMenuOpen)}
            onClose={this.onMenuClose}
            list={!deleteMenuOpen}
          >
            {deleteMenuOpen ? (
              <DeleteMenu
                text="Are you sure you want to delete this order?"
                onCancel={this.onDeleteCancel}
                onSubmit={this.onDeleteSubmit}
              />
            ) : (
              <Fragment>
                <MenuItem
                  onClick={() => {
                    if (archive) {
                      history.push(`/workflow/archive_order/edit/${ID}`);
                    } else {
                      history.push(`/workflow/order/edit/${ID}`);
                    }     
                  }}
                >
                  Edit Order
                </MenuItem>
                <MenuItem
                  onClick={this.onDeleteMenuItemClick}
                  color="warning"
                  disabled={account.Roles.filter(item =>
                    item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                >
                  Delete Order
                </MenuItem>
              </Fragment>
            )}
          </Menu>
        </Toolbar>
      </Paper>
    );
  }
}

OrderViewBarActions.propTypes = {
  ID: PropTypes.number.isRequired,
  Patient: PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    deleteOrder: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  archive: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    toolBar: PropTypes.string.isRequired,
    iconButton: PropTypes.string.isRequired,
    iconBlurb: PropTypes.string.isRequired,
    iconMoreVert: PropTypes.string.isRequired,
  }).isRequired,
};

OrderViewBarActions.defaultProps = {
  archive: false,
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ deleteOrder }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withRouter(withStyles(styles)(OrderViewBarActions)),
);



// WEBPACK FOOTER //
// ./src/components/workflow/OrderViewBar/OrderViewBarActions.js